import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { agentService } from '../services/agent';
import { CreateAgentRequest, UpdateAgentRequest } from '../types/agent';

// Query keys
export const agentKeys = {
  all: ['agents'] as const,
  lists: () => [...agentKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...agentKeys.lists(), params] as const,
  details: () => [...agentKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentKeys.details(), id] as const,
  stats: () => [...agentKeys.all, 'stats'] as const,
  versions: (id: string) => [...agentKeys.detail(id), 'versions'] as const,
  versionList: (id: string, params: Record<string, unknown>) => [...agentKeys.versions(id), params] as const,
  version: (id: string, version: number) => [...agentKeys.versions(id), version] as const,
};

// Hook to fetch agents list with pagination and filters
export function useAgents(params?: {
  page?: number;
  limit?: number;
  type?: string;
  status?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: agentKeys.list(params || {}),
    queryFn: () => agentService.list(params),
  });
}

// Hook to fetch a single agent
export function useAgent(id: string | undefined) {
  return useQuery({
    queryKey: agentKeys.detail(id!),
    queryFn: () => agentService.get(id!),
    enabled: !!id,
  });
}

// Hook to fetch agent stats
export function useAgentStats() {
  return useQuery({
    queryKey: agentKeys.stats(),
    queryFn: () => agentService.getStats(),
  });
}

// Hook to create an agent
export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgentRequest) => agentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent created successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to create agent';
      toast.error(message);
    },
  });
}

// Hook to update an agent
export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentRequest }) =>
      agentService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent updated successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to update agent';
      toast.error(message);
    },
  });
}

// Hook to delete an agent
export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent deleted successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to delete agent';
      toast.error(message);
    },
  });
}

// Hook to activate an agent
export function useActivateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentService.activate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent activated!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to activate agent';
      toast.error(message);
    },
  });
}

// Hook to deactivate an agent
export function useDeactivateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentService.deactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent deactivated!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to deactivate agent';
      toast.error(message);
    },
  });
}

// Hook to clone an agent
export function useCloneAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      agentService.clone(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.stats() });
      toast.success('Agent cloned successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to clone agent';
      toast.error(message);
    },
  });
}

// Hook to fetch agent version history
export function useAgentVersions(
  agentId: string | undefined,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: agentKeys.versionList(agentId!, params || {}),
    queryFn: () => agentService.listVersions(agentId!, params),
    enabled: !!agentId,
  });
}

// Hook to fetch a specific agent version
export function useAgentVersion(agentId: string | undefined, version: number | undefined) {
  return useQuery({
    queryKey: agentKeys.version(agentId!, version!),
    queryFn: () => agentService.getVersion(agentId!, version!),
    enabled: !!agentId && version !== undefined,
  });
}

// Hook to rollback an agent to a specific version
export function useRollbackAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      version,
      reason,
    }: {
      agentId: string;
      version: number;
      reason?: string;
    }) => agentService.rollback(agentId, version, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(variables.agentId) });
      queryClient.invalidateQueries({ queryKey: agentKeys.versions(variables.agentId) });
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      toast.success(`Agent rolled back to version ${variables.version}`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to rollback agent';
      toast.error(message);
    },
  });
}
